// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes — Cost of Inaction theme
export const SCENE_COLORS = {
  scene1: { primaryColor: '#EF4444', accentColor: '#ffffff' },   // Red/White - Missed call
  scene2: { primaryColor: '#F59E0B', accentColor: '#ffffff' },   // Amber/White - Money
  scene3: { primaryColor: '#DC2626', accentColor: '#FCD34D' },   // Deep Red/Gold - Cost
  scene4: { primaryColor: '#3B82F6', accentColor: '#ffffff' },   // Blue/White - Counting
  scene5: { primaryColor: '#ffffff', accentColor: '#EF4444' },   // White/Red - Competitor
  scene6: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - CTA
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },
};

export const SCENE_IMAGES = {
  scene1: 'images/gdm-every-missed-call/scene1-missed-call.png',
  scene2: 'images/gdm-every-missed-call/scene2-money.png',
  scene3: 'images/gdm-every-missed-call/scene3-cost.png',
  scene4: 'images/gdm-every-missed-call/scene4-counting.png',
  scene5: 'images/gdm-every-missed-call/scene5-competitor.png',
  scene6: 'images/gdm-every-missed-call/scene6-report.png',
};

export const REPORT_SLIDES = [
  'images/gdm-quiet-phone/report-slide-01.png',
  'images/gdm-quiet-phone/report-slide-05.png',
  'images/gdm-quiet-phone/report-slide-07.png',
  'images/gdm-quiet-phone/report-slide-12.png',
];

export const DRAMATIC_WORDS = ['money.', '$3,000.', 'thousands', 'Free', 'Report.'];

// Word timings from Whisper — manual corrections: merged "$3"+",000" → "$3,000.", "10" → "Ten"
export const WORD_TIMINGS: WordTiming[] = [
  // Scene 1: Missed call hook
  { text: 'Every', startMs: 0, endMs: 160 },
  { text: 'missed', startMs: 160, endMs: 500 },
  { text: 'call', startMs: 500, endMs: 780 },
  { text: 'costs', startMs: 780, endMs: 1180 },
  { text: 'you', startMs: 1180, endMs: 1360 },
  { text: 'money.', startMs: 1360, endMs: 1780 },

  // Scene 2: Not small money
  { text: 'Not', startMs: 1780, endMs: 2360 },
  { text: 'fifty', startMs: 2360, endMs: 2560 },
  { text: 'bucks.', startMs: 2560, endMs: 3020 },
  { text: 'Not', startMs: 3020, endMs: 3320 },
  { text: 'a', startMs: 3320, endMs: 3440 },
  { text: 'hundred.', startMs: 3440, endMs: 3880 },

  // Scene 3: Real cost
  { text: 'The', startMs: 3880, endMs: 4920 },
  { text: 'average', startMs: 4920, endMs: 5180 },
  { text: 'garage', startMs: 5180, endMs: 5460 },
  { text: 'door', startMs: 5460, endMs: 5700 },
  { text: 'job?', startMs: 5700, endMs: 6180 },
  { text: '$800', startMs: 6180, endMs: 6760 },
  { text: 'to', startMs: 6760, endMs: 7100 },
  { text: '$3,000.', startMs: 7100, endMs: 8140 },

  // Scene 4: How many missed
  { text: 'How', startMs: 8140, endMs: 9780 },
  { text: 'many', startMs: 9780, endMs: 9960 },
  { text: 'calls', startMs: 9960, endMs: 10220 },
  { text: 'did', startMs: 10220, endMs: 10380 },
  { text: 'you', startMs: 10380, endMs: 10500 },
  { text: 'miss', startMs: 10500, endMs: 10680 },
  { text: 'last', startMs: 10680, endMs: 10900 },
  { text: 'month?', startMs: 10900, endMs: 11580 },
  { text: 'Two?', startMs: 11580, endMs: 12160 },
  { text: 'Five?', startMs: 12160, endMs: 12900 },
  { text: 'Ten?', startMs: 12900, endMs: 13400 },

  // Scene 5: Competitor
  { text: "That's", startMs: 13400, endMs: 14180 },
  { text: 'thousands', startMs: 14180, endMs: 14560 },
  { text: 'walking', startMs: 14560, endMs: 14880 },
  { text: 'to', startMs: 14880, endMs: 15080 },
  { text: 'your', startMs: 15080, endMs: 15180 },
  { text: 'competitor.', startMs: 15180, endMs: 15680 },
  { text: "We'll", startMs: 15680, endMs: 15940 },
  { text: 'show', startMs: 15940, endMs: 16080 },
  { text: 'you', startMs: 16080, endMs: 16200 },
  { text: 'exactly', startMs: 16200, endMs: 16440 },
  { text: 'where', startMs: 16440, endMs: 16740 },
  { text: "they're", startMs: 16740, endMs: 16900 },
  { text: 'going.', startMs: 16900, endMs: 17380 },

  // Scene 6: CTA
  { text: 'Free', startMs: 17380, endMs: 17940 },
  { text: 'Market', startMs: 17940, endMs: 18200 },
  { text: 'Report.', startMs: 18200, endMs: 18820 },
  { text: 'Ten', startMs: 18820, endMs: 19080 },
  { text: 'minutes.', startMs: 19080, endMs: 19620 },
  { text: 'Link', startMs: 19620, endMs: 19920 },
  { text: 'below.', startMs: 19920, endMs: 20420 },
];

export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Hook',
    startMs: 0,
    endMs: 1780,
    words: WORD_TIMINGS.slice(0, 6),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Money',
    startMs: 1780,
    endMs: 3880,
    words: WORD_TIMINGS.slice(6, 12),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Cost',
    startMs: 3880,
    endMs: 8140,
    words: WORD_TIMINGS.slice(12, 20),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 4,
    name: 'Counting',
    startMs: 8140,
    endMs: 13400,
    words: WORD_TIMINGS.slice(20, 31),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 5,
    name: 'Competitor',
    startMs: 13400,
    endMs: 17380,
    words: WORD_TIMINGS.slice(31, 44),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 6,
    name: 'CTA',
    startMs: 17380,
    endMs: 20420,
    words: WORD_TIMINGS.slice(44),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

export const LOGO_SCENE = {
  startMs: 20420,
  endMs: 25520,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
